import {
  BillingConfig,
  BillingPortalOptions,
  BillingPortalSession,
  CheckoutOptions,
  CheckoutSession,
  Customer,
  Subscription,
  UsageData,
} from '../types'

/**
 * Teer API client
 */
export class TeerApi {
  private readonly publishableKey: string
  private readonly baseUrl: string = 'https://api.teer.ai/v1'

  constructor(publishableKey: string) {
    this.publishableKey = publishableKey
  }

  /**
   * Make a request to the Teer API
   */
  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    data?: Record<string, any>,
    signal?: AbortSignal
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.publishableKey}`,
    }

    const options: RequestInit = {
      method,
      headers,
      credentials: 'include',
      signal, // Add abort signal to request
    }

    if (data) {
      options.body = JSON.stringify(data)
    }

    try {
      const response = await fetch(url, options)

      // Check if the request was aborted
      if (signal?.aborted) {
        throw new DOMException('Request aborted', 'AbortError')
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `API request failed with status ${response.status}`)
      }

      return response.json()
    } catch (error) {
      // Rethrow AbortError as is
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error
      }

      // Wrap other errors
      if (error instanceof Error) {
        throw error
      }

      throw new Error('Unknown error occurred during API request')
    }
  }

  /**
   * Get billing configuration
   */
  async getBillingConfig(signal?: AbortSignal): Promise<BillingConfig> {
    return this.request<BillingConfig>('GET', '/billing/config', undefined, signal)
  }

  /**
   * Get customer by ID
   */
  async getCustomer(customerId: string, signal?: AbortSignal): Promise<Customer> {
    return this.request<Customer>('GET', `/customers/${customerId}`, undefined, signal)
  }

  /**
   * Get customer subscriptions
   */
  async getSubscriptions(customerId: string, signal?: AbortSignal): Promise<Subscription[]> {
    return this.request<Subscription[]>('GET', `/customers/${customerId}/subscriptions`, undefined, signal)
  }

  /**
   * Create a checkout session
   */
  async createCheckoutSession(options: CheckoutOptions, signal?: AbortSignal): Promise<CheckoutSession> {
    return this.request<CheckoutSession>('POST', '/checkout/sessions', options, signal)
  }

  /**
   * Create a billing portal session
   */
  async createBillingPortalSession(options: BillingPortalOptions, signal?: AbortSignal): Promise<BillingPortalSession> {
    return this.request<BillingPortalSession>('POST', '/billing/portal/sessions', options, signal)
  }

  /**
   * Report usage
   */
  async reportUsage(subscriptionItemId: string, quantity: number, signal?: AbortSignal): Promise<UsageData> {
    return this.request<UsageData>(
      'POST',
      `/subscription-items/${subscriptionItemId}/usage`,
      {
        quantity,
        timestamp: Math.floor(Date.now() / 1000),
      },
      signal
    )
  }
}
